import React, { useState, useEffect } from 'react';
import { useFilters } from '../../cube/filters';

interface ControlPanelProps {}

const PRICE_BUCKETS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

const PRICE_TOTAL_TIERS = [
  '0-300', '300-600', '600-900', '900-1200', '1200-1500',
  '1500-1800', '1800-2100', '2100-2400', '2400-2700', '2700-3000',
  '3000-3300', '3300-3600', '3600-3900', '3900-4200', '4200-4500',
  '4500-4800', '4800-5100', '5100-5400', '5400-5700', '5700-6000',
  '6000-6300', '6300-6600', '6600-6900', '6900-7200', '7200-14100',
  '14100-24000', '24000+'
];

const formatPriceTierLabel = (key: string) => {
  if (key === '0-300') {
    return '300';
  }
  if (key.includes('+')) {
    return key;
  }
  const parts = key.split('-');
  if (parts.length > 1) {
    return parts[1];
  }
  return key;
};

const AREA_TIERS = [
  '0-30m²', '30-50m²', '50-70m²', '70-100m²', 
  '100-150m²', '150-200m²', '200-300m²', '300+m²'
];

const formatAreaSliderLabel = (key: string) => {
  if (key === '0-30m²') {
    return '30m²';
  }
  if (key === '200-300m²') {
    return '300m²';
  }
  if (key.includes('+')) {
    return '301m²+';
  }
  const parts = key.split('-');
  if (parts.length > 1) {
    return parts[1];
  }
  return key;
};

export const ControlPanel: React.FC<ControlPanelProps> = () => {
  const { set } = useFilters();
  
  const [isCompact, setIsCompact] = useState(false);

  const [lowIdx, setLowIdx] = React.useState(0);
  const [highIdx, setHighIdx] = React.useState(PRICE_BUCKETS.length - 1);
  const [lowTierIdx, setLowTierIdx] = React.useState(0);
  const [highTierIdx, setHighTierIdx] = React.useState(PRICE_TOTAL_TIERS.length - 1);
  const [lowAreaIdx, setLowAreaIdx] = React.useState(0);
  const [highAreaIdx, setHighAreaIdx] = React.useState(AREA_TIERS.length - 1);
  
  useEffect(() => {
    const checkWidth = () => setIsCompact(window.innerWidth < 1024);
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  const handleChange = (newLowIdx: number, newHighIdx: number) => {
    setLowIdx(newLowIdx);
    setHighIdx(newHighIdx);

    const selectedBuckets = PRICE_BUCKETS.slice(newLowIdx, newHighIdx + 1);
    set({ priceRange: selectedBuckets.length === PRICE_BUCKETS.length ? undefined : selectedBuckets });
  };

  const handleTierChange = (newLowTierIdx: number, newHighTierIdx: number) => {
    setLowTierIdx(newLowTierIdx);
    setHighTierIdx(newHighTierIdx);

    const selectedTiers = PRICE_TOTAL_TIERS.slice(newLowTierIdx, newHighTierIdx + 1);
    set({ priceTotalTier: selectedTiers.length === PRICE_TOTAL_TIERS.length ? undefined : selectedTiers });
  };

  const handleAreaChange = (newLowAreaIdx: number, newHighAreaIdx: number) => {
    setLowAreaIdx(newLowAreaIdx);
    setHighAreaIdx(newHighAreaIdx);

    const selectedAreas = AREA_TIERS.slice(newLowAreaIdx, newHighAreaIdx + 1);
    set({ areaTier: selectedAreas.length === AREA_TIERS.length ? undefined : selectedAreas });
  };

  const min = 0;
  const max = PRICE_BUCKETS.length - 1;
  const getPercent = (idx: number) => (idx * 100) / max;
  const left = getPercent(lowIdx);
  const width = getPercent(highIdx) - left;

  const lowLabel = PRICE_BUCKETS[lowIdx];
  const highLabel = highIdx === max ? '100+' : String(PRICE_BUCKETS[highIdx] + 10);

  const tierMin = 0;
  const tierMax = PRICE_TOTAL_TIERS.length - 1;
  const getTierPercent = (idx: number) => (idx * 100) / tierMax;
  const tierLeft = getTierPercent(lowTierIdx);
  const tierWidth = getTierPercent(highTierIdx) - tierLeft;

  const lowTierLabel = formatPriceTierLabel(PRICE_TOTAL_TIERS[lowTierIdx]);
  const highTierLabel = formatPriceTierLabel(PRICE_TOTAL_TIERS[highTierIdx]);

  const areaMin = 0;
  const areaMax = AREA_TIERS.length - 1;
  const getAreaPercent = (idx: number) => (idx * 100) / areaMax;
  const areaLeft = getAreaPercent(lowAreaIdx);
  const areaWidth = getAreaPercent(highAreaIdx) - areaLeft;

  const lowAreaLabel = formatAreaSliderLabel(AREA_TIERS[lowAreaIdx]);
  const highAreaLabel = formatAreaSliderLabel(AREA_TIERS[highAreaIdx]);

  const priceRangeSection = (
    <div className="control-group">
      <div className="range-slider">
        <div className="range-track" />
        <div className="range-highlight" style={{ left: `${left}%`, width: `${width}%` }} />
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={lowIdx}
          onChange={(e) => {
            const newLow = Math.min(Number(e.target.value), highIdx);
            handleChange(newLow, highIdx);
          }}
          className="range-input"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={highIdx}
          onChange={(e) => {
            const newHigh = Math.max(Number(e.target.value), lowIdx);
            handleChange(lowIdx, newHigh);
          }}
          className="range-input"
        />
      </div>
      <div className="price-range-display">
        R${lowLabel} - R${highLabel}
      </div>
    </div>
  );

  const priceTiersSection = (
    <div className="control-group">
      <div className="range-slider">
        <div className="range-track" />
        <div className="range-highlight" style={{ left: `${tierLeft}%`, width: `${tierWidth}%` }} />
        <input
          type="range"
          min={tierMin}
          max={tierMax}
          step={1}
          value={lowTierIdx}
          onChange={(e) => {
            const newLow = Math.min(Number(e.target.value), highTierIdx);
            handleTierChange(newLow, highTierIdx);
          }}
          className="range-input"
        />
        <input
          type="range"
          min={tierMin}
          max={tierMax}
          step={1}
          value={highTierIdx}
          onChange={(e) => {
            const newHigh = Math.max(Number(e.target.value), lowTierIdx);
            handleTierChange(lowTierIdx, newHigh);
          }}
          className="range-input"
        />
      </div>
      <div className="price-range-display">
        {lowTierLabel} - {highTierLabel}
      </div>
    </div>
  );

  const areaTierSection = (
    <div className="control-group">
      <div className="range-slider">
        <div className="range-track" />
        <div className="range-highlight" style={{ left: `${areaLeft}%`, width: `${areaWidth}%` }} />
        <input
          type="range"
          min={areaMin}
          max={areaMax}
          step={1}
          value={lowAreaIdx}
          onChange={(e) => {
            const newLow = Math.min(Number(e.target.value), highAreaIdx);
            handleAreaChange(newLow, highAreaIdx);
          }}
          className="range-input"
        />
        <input
          type="range"
          min={areaMin}
          max={areaMax}
          step={1}
          value={highAreaIdx}
          onChange={(e) => {
            const newHigh = Math.max(Number(e.target.value), lowAreaIdx);
            handleAreaChange(lowAreaIdx, newHigh);
          }}
          className="range-input"
        />
      </div>
      <div className="price-range-display">
        {lowAreaLabel} - {highAreaLabel}
      </div>
    </div>
  );

  return (
    <div className="control-panel">
      {isCompact ? (
        <>
          <div className="panel-section">
            <h3>Preço por m²</h3>
            {priceRangeSection}
          </div>
          <div className="panel-section">
            <h3>Preço Total</h3>
            {priceTiersSection}
          </div>
          <div className="panel-section">
            <h3>Tamanho do Imóvel</h3>
            {areaTierSection}
          </div>
        </>
      ) : (
        <>
          <div className="panel-section">
            <h3>Filtro de Aluguel (R$/m²)</h3>
            {priceRangeSection}
          </div>
          <div className="panel-section">
            <h3>Filtro de Aluguel</h3>
            {priceTiersSection}
          </div>
          <div className="panel-section">
            <h3>Tamanho do Imóvel</h3>
            {areaTierSection}
          </div>
        </>
      )}
    </div>
  );
};
